import { type ReactNode, useState } from 'react';

import { TERMS_VERSION } from '@/api';
import { Button, Dialog } from '@/components/ui';
import { cn } from '@/lib/utils';

export type LegalDocument = 'terms' | 'privacy';

/*
  Тексти — заповнювач (рішення 2026-09-29): юридичних документів ще немає, а
  прапорець згоди при реєстрації вже обовʼязковий. Заголовки розділів —
  справжні: це те, що документ мусить покрити, щойно його напише юрист.
  Змінився текст — змінюй і TERMS_VERSION у packages/contracts.
*/
const LOREM = [
	'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante venenatis dapibus posuere velit aliquet. Maecenas faucibus mollis interdum, sed posuere consectetur est at lobortis.',
	'Cras mattis consectetur purus sit amet fermentum. Nullam quis risus eget urna mollis ornare vel eu leo. Donec ullamcorper nulla non metus auctor fringilla, vestibulum id ligula porta felis euismod semper.',
] as const;

const DOCUMENTS: Record<LegalDocument, { title: string; sections: readonly string[] }> = {
	terms: {
		title: 'Terms & Conditions',
		sections: [
			'Your account',
			'Deposits and how they are held',
			'Bidding on your behalf',
			'Winning, invoices and payment deadlines',
			'Unpaid invoices and deposit forfeiture',
			'Shipping, storage and delivery',
			'Liability for vehicle condition',
			'Changes to these terms',
		],
	},
	privacy: {
		title: 'Privacy Policy',
		sections: [
			'What we collect',
			'Why we use it',
			'Who we share it with',
			'How long we keep it',
			'Your rights',
			'Contacting us',
		],
	},
};

function LegalBody({ doc }: { doc: LegalDocument }): ReactNode {
	return (
		<div className="flex flex-col gap-5 text-sm leading-relaxed text-fg-muted">
			<p className="rounded-control border border-warning/30 bg-warning/10 px-3 py-2.5 text-[0.8125rem] text-fg">
				Draft placeholder. The final text is being prepared by our legal team and will replace this page before launch.
			</p>
			{DOCUMENTS[doc].sections.map((heading, index) => (
				<section key={heading} className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-fg">
						{index + 1}. {heading}
					</h3>
					{LOREM.map((paragraph) => (
						<p key={paragraph}>{paragraph}</p>
					))}
				</section>
			))}
		</div>
	);
}

/**
 * Посилання на юридичний документ, що відкриває його в модалці, а не на
 * окремій сторінці: на реєстрації дилер не має втрачати заповнену форму,
 * щоб прочитати умови, під якими підписується.
 *
 * Кнопка, а не `<a href="#">`: посилання в нікуди не має ні адреси, ні дії.
 * Усередині `<label>` кнопка прапорця не перемикає — клік по інтерактивному
 * нащадку мітка не пересилає.
 */
export function LegalLink({ doc, className, children }: { doc: LegalDocument; className?: string; children: ReactNode }): ReactNode {
	const [open, setOpen] = useState(false);
	const { title } = DOCUMENTS[doc];

	return (
		<>
			<button type="button" onClick={() => setOpen(true)} className={cn('cursor-pointer', className)}>
				{children}
			</button>
			<Dialog
				open={open}
				onOpenChange={setOpen}
				title={title}
				description={`Version ${TERMS_VERSION}`}
				className="w-[min(40rem,92vw)]"
				footer={
					<div className="flex justify-end">
						<Button size="sm" onClick={() => setOpen(false)}>
							Close
						</Button>
					</div>
				}
			>
				<LegalBody doc={doc} />
			</Dialog>
		</>
	);
}
